// One profile setting's control, chosen by its schema kind: a checkbox, a
// segmented choice or select, a number, or text. A nullable setting gets a
// checkbox for its null meaning (e.g. "Use each guide's speed").

import type { FieldSpec, FormValue } from "./profileForm";
import { startValue } from "./profileForm";

const SEG_MAX = 3;

export function SettingField({
  spec,
  value,
  error,
  defaults,
  onChange,
}: {
  spec: FieldSpec;
  value: FormValue;
  error?: string;
  defaults: Record<string, unknown>;
  onChange: (value: FormValue) => void;
}) {
  const id = `ve-set-${spec.name}`;
  const help = spec.help && <div className="ve-help">{spec.help}</div>;
  const err = error && <div className="ve-field-error">{error}</div>;

  if (spec.kind === "boolean" && !spec.nullable) {
    return (
      <div className="ve-pfield">
        <label className="ve-check">
          <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          <span>
            {spec.label} <span className="mono muted ve-small">{spec.name}</span>
            {help}
          </span>
        </label>
        {err}
      </div>
    );
  }

  const isNull = value === null;
  const text = typeof value === "string" ? value : "";
  let control = null;
  if (!isNull) {
    if (spec.kind === "boolean") {
      control = (
        <label className="ve-check">
          <input type="checkbox" checked={value === true} onChange={(e) => onChange(e.target.checked)} />
          <span>On</span>
        </label>
      );
    } else if (spec.kind === "enum" && (spec.options?.length ?? 0) <= SEG_MAX) {
      control = (
        <div className="seg" role="group" aria-labelledby={`${id}-l`}>
          {spec.options!.map((o) => (
            <button
              key={o}
              type="button"
              className={text === o ? "active" : ""}
              aria-pressed={text === o}
              onClick={() => onChange(o)}
            >
              {o}
            </button>
          ))}
        </div>
      );
    } else if (spec.kind === "enum") {
      control = (
        <select id={id} className="mono" value={text} onChange={(e) => onChange(e.target.value)}>
          {spec.options!.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      );
    } else if (spec.kind === "integer" || spec.kind === "number") {
      control = (
        <input
          id={id}
          type="number"
          min={spec.min}
          max={spec.max}
          step={spec.step}
          value={text}
          aria-invalid={Boolean(error)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    } else {
      control = (
        <input
          id={id}
          type="text"
          className={spec.kind === "list" ? "" : "mono"}
          value={text}
          aria-invalid={Boolean(error)}
          onChange={(e) => onChange(e.target.value)}
        />
      );
    }
  }

  const bounds =
    (spec.kind === "integer" || spec.kind === "number") && spec.min !== undefined && spec.max !== undefined
      ? ` · ${spec.min}–${spec.max}`
      : "";
  return (
    <div className={`ve-pfield${spec.kind === "list" ? " wide" : ""}`}>
      <label id={`${id}-l`} htmlFor={id}>
        {spec.label} <span className="mono">{spec.name}</span>
        {bounds}
      </label>
      {spec.nullable && (
        <label className="ve-check" style={{ marginTop: 0 }}>
          <input
            type="checkbox"
            checked={isNull}
            onChange={(e) => onChange(e.target.checked ? null : startValue(spec, defaults))}
          />
          <span>{spec.nullLabel}</span>
        </label>
      )}
      {control}
      {help}
      {err}
    </div>
  );
}
